import { useState } from 'react';
import { ActionIcon, Badge, Button, FileInput, Group, Modal, Paper, Select, SimpleGrid, Stack, Table, Text, Textarea, TextInput, Tooltip } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconArchive, IconDownload, IconEye, IconUpload } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CATEGORIAS_DOCUMENTO, hoy } from '@gestionpyme/dominio';
import { abrirDocumento, api, descargar } from '../api';
import { avisarError, avisarOk, Cabecera, fechaCorta, mostrarAvisos } from '../comun';
import { ACEPTA_ARCHIVOS, subirDocumento } from '../componentes/Adjuntos';
import type { Documento } from '../tipos';

const OPCIONES_CATEGORIA = CATEGORIAS_DOCUMENTO.lista.map((c) => ({ value: c.codigo, label: c.nombre }));
const aISO = (d: Date | string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : null);
const tamano = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);

export function PaginaDocumentos() {
  const qc = useQueryClient();
  const [categoria, setCategoria] = useState<string | null>(null);
  const [estado, setEstado] = useState('vigente');
  const [q, setQ] = useState('');
  const [subir, setSubir] = useState(false);
  const params = new URLSearchParams({ estado, ...(categoria ? { categoria } : {}), ...(q ? { q } : {}) });
  const lista = useQuery({ queryKey: ['documentos', estado, categoria, q], queryFn: () => api.get<Documento[]>(`/documentos?${params}`) });
  const hoyISO = hoy();

  const cambiarEstado = async (d: Documento) => {
    try {
      await api.post(`/documentos/${d.id}/${d.estado === 'vigente' ? 'archivar' : 'reactivar'}`);
      await qc.invalidateQueries({ queryKey: ['documentos'] });
    } catch (e) {
      avisarError(e);
    }
  };

  return (
    <>
      <Cabecera titulo="Documentos" descripcion="Justificantes y archivo legal. Los documentos no se borran: se archivan.">
        <Button leftSection={<IconUpload size={16} />} onClick={() => setSubir(true)}>
          Subir documento
        </Button>
      </Cabecera>
      <Paper withBorder p="sm" mb="md">
        <Group wrap="wrap">
          <Select placeholder="Todas las categorías" data={OPCIONES_CATEGORIA} value={categoria} onChange={setCategoria} clearable w={260} />
          <Select
            data={[
              { value: 'vigente', label: 'Vigentes' },
              { value: 'archivado', label: 'Archivados' },
              { value: 'todos', label: 'Todos' },
            ]}
            value={estado}
            onChange={(v) => setEstado(v ?? 'vigente')}
            allowDeselect={false}
            w={140}
          />
          <TextInput placeholder="Buscar título, archivo o notas…" value={q} onChange={(e) => setQ(e.currentTarget.value)} style={{ flex: 1, minWidth: 180 }} />
        </Group>
      </Paper>
      <Paper withBorder>
        <Table.ScrollContainer minWidth={700}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Título</Table.Th>
                <Table.Th>Categoría</Table.Th>
                <Table.Th>Fecha</Table.Th>
                <Table.Th>Vence</Table.Th>
                <Table.Th>Vínculos</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(lista.data ?? []).map((d) => {
                const vencido = d.fechaVencimiento && d.fechaVencimiento < hoyISO;
                const pronto = d.fechaVencimiento && !vencido && dayjs(d.fechaVencimiento).diff(hoyISO, 'day') <= 30;
                return (
                  <Table.Tr key={d.id} style={{ opacity: d.estado === 'archivado' ? 0.55 : 1 }}>
                    <Table.Td>
                      <Text size="sm">{d.titulo}</Text>
                      <Text size="xs" c="dimmed">
                        {d.nombreArchivo} · {tamano(d.tamano)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{CATEGORIAS_DOCUMENTO.nombre(d.categoria) ?? d.categoria}</Text>
                    </Table.Td>
                    <Table.Td>{fechaCorta(d.fechaDocumento)}</Table.Td>
                    <Table.Td>
                      {d.fechaVencimiento ? (
                        <Badge color={vencido ? 'red' : pronto ? 'orange' : 'gray'} variant="light">
                          {fechaCorta(d.fechaVencimiento)}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                    <Table.Td>{d.vinculos.length ? d.vinculos.map((v) => v.entidad).join(', ') : '—'}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="Ver">
                          <ActionIcon variant="subtle" onClick={() => void abrirDocumento(d.id).catch(avisarError)}>
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Descargar">
                          <ActionIcon variant="subtle" onClick={() => void descargar(`/documentos/${d.id}/archivo?descargar=1`, d.nombreArchivo).catch(avisarError)}>
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={d.estado === 'vigente' ? 'Archivar' : 'Reactivar'}>
                          <ActionIcon variant="subtle" color="gray" onClick={() => void cambiarEstado(d)}>
                            <IconArchive size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {lista.data?.length === 0 && (
          <Text c="dimmed" ta="center" p="lg">
            No hay documentos.
          </Text>
        )}
      </Paper>
      <Modal opened={subir} onClose={() => setSubir(false)} title="Subir documento" size="lg">
        <FormularioSubida
          onListo={() => {
            setSubir(false);
            void qc.invalidateQueries({ queryKey: ['documentos'] });
          }}
        />
      </Modal>
    </>
  );
}

function FormularioSubida({ onListo }: { onListo: () => void }) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [d, setD] = useState({ categoria: 'licencia', titulo: '', fechaDocumento: null as string | null, fechaVencimiento: null as string | null, notas: '' });
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    if (!archivo) return;
    setEnviando(true);
    try {
      const r = await subirDocumento(archivo, { ...d, titulo: d.titulo || archivo.name, notas: d.notas || null });
      mostrarAvisos([r.aviso]);
      avisarOk('Documento guardado');
      onListo();
    } catch (e) {
      avisarError(e);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Stack>
      <FileInput label="Archivo" placeholder="PDF, imagen…" accept={ACEPTA_ARCHIVOS} value={archivo} onChange={setArchivo} required clearable />
      <Select label="Categoría" data={OPCIONES_CATEGORIA} value={d.categoria} onChange={(v) => setD({ ...d, categoria: v ?? 'otro' })} allowDeselect={false} />
      <TextInput label="Título" placeholder={archivo?.name} value={d.titulo} onChange={(e) => setD({ ...d, titulo: e.currentTarget.value })} />
      <SimpleGrid cols={2}>
        <DateInput label="Fecha del documento" value={d.fechaDocumento} onChange={(v) => setD({ ...d, fechaDocumento: aISO(v) })} valueFormat="DD/MM/YYYY" clearable />
        <DateInput
          label="Fecha de vencimiento"
          description="Recibirás un aviso antes"
          value={d.fechaVencimiento}
          onChange={(v) => setD({ ...d, fechaVencimiento: aISO(v) })}
          valueFormat="DD/MM/YYYY"
          clearable
        />
      </SimpleGrid>
      <Textarea label="Notas" autosize minRows={2} value={d.notas} onChange={(e) => setD({ ...d, notas: e.currentTarget.value })} />
      <Group justify="flex-end">
        <Button onClick={enviar} loading={enviando} disabled={!archivo}>
          Guardar
        </Button>
      </Group>
    </Stack>
  );
}
